import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, KeyRound, Pencil, Users, Map, Layers, FileCheck, Search, ChevronDown, ChevronRight, Upload, Download, Video } from 'lucide-react';
import Navbar from '../Layout/Navbar';
import {
  getUsers, createUser, updateUser, resetUserPassword, deleteUser,
  getClients, getAdminStats, importUsersCSV, manageUserTrial, getWebinarAttendees,
  deleteWebinarAttendee, getMaintenance, setMaintenance as apiSetMaintenance,
} from '../../api/client';

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-navy/5 flex items-center justify-center">
        <Icon className="w-4.5 h-4.5 text-navy/50" />
      </div>
      <div>
        <p className="text-xl font-heading font-bold text-txt-primary leading-none">{value}</p>
        <p className="text-[11px] text-txt-secondary uppercase mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function checkPassword(pw) {
  if (!pw || pw.length < 12) return 'Password must be at least 12 characters';
  if (!/[A-Z]/.test(pw)) return 'Must include an uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Must include a lowercase letter';
  if (!/[0-9]/.test(pw)) return 'Must include a number';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Must include a special character';
  return null;
}

export default function AdminScreen() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [attPage, setAttPage] = useState(1);
  const [maint, setMaint] = useState(false);
  const [maintBusy, setMaintBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create user form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'CLIENT', clientId: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit user
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', clientId: '' });
  const [editError, setEditError] = useState('');

  // Password reset
  const [resetId, setResetId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // Search, sort & pagination
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Bulk select
  const [selected, setSelected] = useState(new Set());

  // Trial management
  const [trialId, setTrialId] = useState(null);
  const [trialDays, setTrialDays] = useState('30');

  // Client list
  const [clientsOpen, setClientsOpen] = useState(false);
  const [clientSort, setClientSort] = useState('name'); // 'name' | 'users' | 'maps'

  useEffect(() => {
    const stored = localStorage.getItem('user');
    const user = stored ? JSON.parse(stored) : null;
    if (!user || user.role !== 'ADMIN') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [navigate]);

  async function loadData() {
    try {
      const [usersData, clientsData, statsData, attendeesData, maintData] = await Promise.all([
        getUsers(), getClients(), getAdminStats(),
        getWebinarAttendees().catch(() => []),
        getMaintenance().catch(() => ({ on: false })),
      ]);
      setUsers(usersData);
      setClients(clientsData);
      setStats(statsData);
      setAttendees(attendeesData || []);
      setMaint(!!(maintData && maintData.on));
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  function clientName(clientId) {
    return clients.find((c) => c.id === clientId)?.name || '';
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreateError('');
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setCreateError('All fields are required');
      return;
    }
    const pwErr = checkPassword(form.password);
    if (pwErr) {
      setCreateError(pwErr);
      return;
    }
    if (form.role === 'CLIENT' && !form.clientId) {
      setCreateError('Client users must be assigned to a client');
      return;
    }
    setCreating(true);
    try {
      const user = await createUser({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        clientId: form.role === 'ADMIN' ? null : form.clientId,
      });
      setUsers((prev) => [user, ...prev]);
      setForm({ name: '', email: '', password: '', role: 'CLIENT', clientId: '' });
      setShowCreate(false);
    } catch (err) {
      setCreateError(err.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  }

  function startEdit(u) {
    setEditId(u.id);
    setEditForm({ name: u.name, email: u.email, role: u.role, clientId: u.clientId || '' });
    setEditError('');
    setResetId(null);
  }

  async function saveEdit(userId) {
    setEditError('');
    if (editForm.role === 'CLIENT' && !editForm.clientId) {
      setEditError('Client users must be assigned to a client');
      return;
    }
    try {
      const updated = await updateUser(userId, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        role: editForm.role,
        clientId: editForm.role === 'ADMIN' ? null : editForm.clientId,
      });
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      setEditId(null);
    } catch (err) {
      setEditError(err.message || 'Failed to update user');
    }
  }

  async function handleResetPassword(userId) {
    const pwErr = checkPassword(newPassword);
    if (pwErr) return;
    setResetting(true);
    try {
      await resetUserPassword(userId, newPassword);
      setResetId(null);
      setNewPassword('');
    } catch (err) {
      console.error('Failed to reset password:', err);
    } finally {
      setResetting(false);
    }
  }

  async function handleDelete(userId, userName) {
    if (!window.confirm(`Delete user "${userName}"?\n\nThis will also delete all their clients and maps. This cannot be undone.`)) return;
    try {
      await deleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setSelected((prev) => { const next = new Set(prev); next.delete(userId); return next; });
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  }

  async function handleBulkDelete() {
    if (selected.size === 0) return;
    const adminId = currentUser.id;
    const toDelete = [...selected].filter((id) => id !== adminId);
    if (toDelete.length === 0) { alert('Cannot delete your own account.'); return; }
    if (!window.confirm(`Delete ${toDelete.length} selected user(s)?\n\nThis will also delete their clients and maps. This cannot be undone.`)) return;
    let deleted = 0;
    for (const id of toDelete) {
      try {
        await deleteUser(id);
        deleted++;
      } catch (err) {
        console.error(`Failed to delete user ${id}:`, err);
      }
    }
    setSelected(new Set());
    setUsers((prev) => prev.filter((u) => !toDelete.includes(u.id)));
    alert(`Deleted ${deleted} of ${toDelete.length} users.`);
  }

  async function toggleMaintenance() {
    const next = !maint;
    if (next && !window.confirm('Put the site into maintenance mode?\n\nVisitors will see a "back shortly" page. You keep full access to test.')) return;
    setMaintBusy(true);
    try {
      const res = await apiSetMaintenance(next);
      setMaint(!!res.on);
    } catch (err) {
      alert('Failed to update maintenance mode.');
    } finally {
      setMaintBusy(false);
    }
  }

  async function deleteAttendee(a) {
    if (!window.confirm(`Delete the webinar registration for ${a.email}?`)) return;
    try {
      await deleteWebinarAttendee(a.id);
      setAttendees((prev) => prev.filter((x) => x.id !== a.id));
    } catch (err) {
      alert('Failed to delete the registration.');
    }
  }

  function downloadAttendeesCSV() {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['Name', 'Email', 'Problem', 'Registered'];
    const rows = attendees.map((a) =>
      [a.name || '', a.email, a.problem || '', new Date(a.createdAt).toISOString()].map(esc).join(',')
    );
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'webinar-registrations.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <Navbar />

      <main className="flex-1 px-6 py-8 max-w-4xl mx-auto w-full">
        {/* Maintenance mode */}
        <div className={`mb-6 rounded-lg border px-5 py-4 flex items-center justify-between gap-4 ${maint ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
          <div>
            <p className="font-heading font-semibold text-txt-primary flex items-center gap-2">
              Maintenance mode
              <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${maint ? 'bg-amber-200 text-amber-900' : 'bg-gray-100 text-txt-secondary'}`}>
                {maint ? 'On' : 'Off'}
              </span>
            </p>
            <p className="text-xs text-txt-secondary mt-0.5">
              {maint
                ? 'Visitors see a "back shortly" page. You keep full access to test.'
                : 'Turn on to show visitors a maintenance page while you deploy or test.'}
            </p>
          </div>
          <button
            onClick={toggleMaintenance}
            disabled={maintBusy}
            className={`text-sm px-3 py-2 rounded-lg font-semibold disabled:opacity-50 flex-shrink-0 ${maint ? 'bg-amber-600 text-white hover:bg-amber-700' : 'btn-gold'}`}
          >
            {maintBusy ? '...' : maint ? 'Exit maintenance' : 'Enter maintenance'}
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mb-8">
            <div className="grid grid-cols-5 gap-3 mb-4">
              <StatCard icon={Users} label="Users" value={stats.totals.users} />
              <StatCard icon={Layers} label="Clients" value={stats.totals.clients} />
              <StatCard icon={Map} label="Maps" value={stats.totals.maps} />
              <StatCard icon={FileCheck} label="Completed" value={stats.totals.completedMaps} />
              <StatCard icon={Layers} label="Total Concepts" value={stats.totals.tiles} />
            </div>

            {/* Client breakdown — sortable + collapsible */}
            {stats.mapsPerClient.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-4 py-3 flex items-center justify-between">
                  <button
                    onClick={() => setClientsOpen(!clientsOpen)}
                    className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                  >
                    {clientsOpen ? <ChevronDown className="w-3.5 h-3.5 text-txt-secondary" /> : <ChevronRight className="w-3.5 h-3.5 text-txt-secondary" />}
                    <h3 className="text-xs font-heading font-semibold uppercase text-txt-secondary">By Client</h3>
                    <span className="text-[10px] text-txt-secondary/60">{stats.mapsPerClient.length}</span>
                  </button>
                  {clientsOpen && (
                    <div className="flex items-center gap-1 text-[10px] text-txt-secondary">
                      {['name', 'users', 'maps'].map((key) => (
                        <button
                          key={key}
                          onClick={() => setClientSort(key)}
                          className={`px-1.5 py-0.5 rounded ${clientSort === key ? 'bg-navy/10 text-txt-primary font-semibold' : 'hover:bg-navy/5'}`}
                        >
                          {key}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {clientsOpen && (
                  <div className="px-4 pb-3">
                    <div className="space-y-2">
                      {[...stats.mapsPerClient]
                        .sort((a, b) => {
                          if (clientSort === 'name') return a.name.localeCompare(b.name);
                          if (clientSort === 'users') return b.users - a.users;
                          return b.maps - a.maps;
                        })
                        .map((c) => (
                        <div key={c.id} className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-txt-primary font-medium">{c.name}</span>
                          <span className="text-txt-secondary text-xs">
                            {c.users} {c.users === 1 ? 'user' : 'users'}, {c.maps} {c.maps === 1 ? 'map' : 'maps'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recent activity */}
            {stats.recentActivity.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-3">
                <h3 className="text-xs font-heading font-semibold uppercase text-txt-secondary mb-3">Recent Activity</h3>
                <div className="space-y-1.5">
                  {stats.recentActivity.slice(0, 3).map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-txt-primary font-medium truncate">{m.name}</span>
                        <span className="text-txt-secondary/60 text-xs shrink-0">{m.client}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-txt-secondary shrink-0">
                        <span>{m.tiles} concepts</span>
                        <span>{m.versions} {m.versions === 1 ? 'version' : 'versions'}</span>
                        <span>{new Date(m.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Webinar registrations */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Video className="w-4 h-4 text-txt-secondary" />
              <h2 className="text-lg font-heading font-semibold text-txt-primary">Webinar Registrations</h2>
              <span className="text-xs text-txt-secondary bg-gray-100 px-2 py-0.5 rounded-full">{attendees.length}</span>
            </div>
            {attendees.length > 0 && (
              <button
                onClick={downloadAttendeesCSV}
                className="flex items-center gap-1.5 text-sm text-txt-secondary border border-navy/10 bg-white hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download CSV
              </button>
            )}
          </div>
          {attendees.length === 0 ? (
            <p className="text-sm text-txt-secondary bg-white rounded-lg border border-gray-200 px-5 py-4">
              No registrations yet.
            </p>
          ) : (() => {
            const totalPages = Math.ceil(attendees.length / PAGE_SIZE);
            const safePage = Math.min(attPage, totalPages || 1);
            const paginated = attendees.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
            return (
              <>
                <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {paginated.map((a) => (
                    <div key={a.id} className="px-5 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <span className="font-semibold text-txt-primary">{a.name || '(no name)'}</span>
                          <span className="text-sm text-txt-secondary ml-2">{a.email}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs text-txt-secondary">{new Date(a.createdAt).toLocaleDateString()}</span>
                          <button
                            onClick={() => deleteAttendee(a)}
                            className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                            title="Delete registration"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {a.problem && (
                        <p className="text-sm text-txt-secondary mt-1 whitespace-pre-wrap">{a.problem}</p>
                      )}
                    </div>
                  ))}
                </div>
                {attendees.length > PAGE_SIZE && (
                  <div className="flex items-center justify-end gap-3 mt-2 text-sm text-txt-secondary">
                    <span className="text-xs">Showing {(safePage - 1) * PAGE_SIZE + 1} to {Math.min(safePage * PAGE_SIZE, attendees.length)} of {attendees.length}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setAttPage(1)} disabled={safePage <= 1}
                        className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&laquo;</button>
                      <button onClick={() => setAttPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                        className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&lsaquo;</button>
                      <span className="px-3 py-1 text-txt-primary font-semibold">{safePage} / {totalPages}</span>
                      <button onClick={() => setAttPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                        className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&rsaquo;</button>
                      <button onClick={() => setAttPage(totalPages)} disabled={safePage >= totalPages}
                        className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&raquo;</button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Users header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-heading font-semibold text-txt-primary">User Accounts</h2>
            <span className="text-xs text-txt-secondary bg-gray-100 px-2 py-0.5 rounded-full">{users.length}</span>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-lg border border-navy/10 text-sm py-1.5 pl-3 pr-8 focus:border-gold focus:ring-1 focus:ring-gold bg-white text-txt-secondary"
            >
              <option value="name">Sort: Name</option>
              <option value="email">Sort: Email</option>
              <option value="client">Sort: Client</option>
              <option value="role">Sort: Role</option>
              <option value="date">Sort: Newest</option>
              <option value="trial">Sort: Trial Status</option>
            </select>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-txt-secondary/50" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search users..."
                className="pl-8 pr-3 py-1.5 rounded-lg border border-navy/10 text-sm w-52
                  focus:border-gold focus:ring-1 focus:ring-gold bg-white placeholder:text-gray-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setShowCreate(!showCreate); setEditId(null); setResetId(null); }}
                className="btn-gold text-sm"
              >
                {showCreate ? 'Cancel' : '+ New User'}
              </button>
              <label className="flex items-center gap-1.5 text-sm text-txt-secondary border border-navy/10 bg-white hover:bg-gray-50 px-3 py-2 rounded-lg cursor-pointer transition-colors">
                <Upload className="w-3.5 h-3.5" />
                CSV Import
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const text = await file.text();
                    const lines = text.split('\n').filter((l) => l.trim());
                    if (lines.length < 2) { setError('CSV must have a header row and at least one data row'); return; }
                    const headers = lines[0].split(',').map((h) => h.trim());
                    const rows = lines.slice(1).map((line) => {
                      const vals = line.split(',').map((v) => v.trim());
                      const row = {};
                      headers.forEach((h, i) => { row[h] = vals[i] || ''; });
                      return row;
                    }).filter((r) => r.email);
                    if (rows.length === 0) { setError('No valid rows found in CSV'); return; }
                    try {
                      const result = await importUsersCSV(rows);
                      alert(`Import complete: ${result.created} created, ${result.skipped} skipped.${result.errors?.length ? '\n\nErrors:\n' + result.errors.join('\n') : ''}`);
                      loadData();
                    } catch (err) {
                      setError(err.message || 'CSV import failed');
                    }
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        {/* Create user form */}
        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white rounded-xl border border-gray-200 p-5 mb-6 space-y-4">
            <h3 className="font-heading font-semibold text-txt-primary">Create User Account</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">Initial Password</label>
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 12, upper/lower/number/special"
                  className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value, clientId: e.target.value === 'ADMIN' ? '' : form.clientId })}
                  className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                >
                  <option value="CLIENT">Client</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
            </div>
            {form.role === 'CLIENT' && (
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">
                  Assign to Client <span className="text-red-400">*</span>
                </label>
                <select
                  value={form.clientId}
                  onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                  className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                >
                  <option value="">Select a client...</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {clients.length === 0 && (
                  <p className="text-xs text-txt-secondary mt-1">No clients yet. Create one from the client selector first.</p>
                )}
              </div>
            )}
            <p className="text-xs text-txt-secondary">
              Passwords are bcrypt-hashed and never stored in plain text. Require 12+ characters with uppercase, lowercase, number, and special character.
            </p>
            {createError && <p className="text-red-500 text-sm">{createError}</p>}
            <button
              type="submit"
              disabled={creating}
              className="btn-gold text-sm disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Account'}
            </button>
          </form>
        )}

        {/* User list */}
        {loading ? (
          <div className="text-center text-txt-secondary py-8">Loading...</div>
        ) : (
          <div className="space-y-2">
            {(() => {
              const filtered = users
                .filter((u) => {
                  if (!search.trim()) return true;
                  const q = search.toLowerCase();
                  return (
                    u.name.toLowerCase().includes(q) ||
                    u.email.toLowerCase().includes(q) ||
                    u.role.toLowerCase().includes(q) ||
                    (u.clientId && clientName(u.clientId).toLowerCase().includes(q))
                  );
                })
                .sort((a, b) => {
                  switch (sortBy) {
                    case 'name': return a.name.localeCompare(b.name);
                    case 'email': return a.email.localeCompare(b.email);
                    case 'client': return (clientName(a.clientId) || '').localeCompare(clientName(b.clientId) || '');
                    case 'role': return a.role.localeCompare(b.role);
                    case 'date': return new Date(b.createdAt) - new Date(a.createdAt);
                    case 'trial': {
                      const aT = a.trialExpiresAt ? 1 : 0;
                      const bT = b.trialExpiresAt ? 1 : 0;
                      if (aT !== bT) return bT - aT;
                      return a.name.localeCompare(b.name);
                    }
                    default: return 0;
                  }
                });
              const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
              const safePage = Math.min(page, totalPages || 1);
              const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
              return (<>
              <div className="flex items-center justify-between mb-2 text-sm text-txt-secondary">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox"
                      checked={paginated.length > 0 && paginated.every((u) => selected.has(u.id))}
                      onChange={(e) => {
                        const next = new Set(selected);
                        paginated.forEach((u) => { e.target.checked ? next.add(u.id) : next.delete(u.id); });
                        setSelected(next);
                      }}
                      className="rounded border-gray-300 text-gold focus:ring-gold"
                    />
                    <span className="text-xs">Select page</span>
                  </label>
                  {selected.size > 0 && (
                    <button onClick={handleBulkDelete}
                      className="text-xs text-red-500 hover:text-red-700 font-semibold">
                      Delete {selected.size} selected
                    </button>
                  )}
                </div>
              {filtered.length > PAGE_SIZE && (
                <div className="flex items-center gap-3">
                  <span className="text-xs">Showing {(safePage - 1) * PAGE_SIZE + 1} to {Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPage(1)} disabled={safePage <= 1}
                      className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&laquo;</button>
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                      className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&lsaquo;</button>
                    <span className="px-3 py-1 text-txt-primary font-semibold">{safePage} / {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                      className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&rsaquo;</button>
                    <button onClick={() => setPage(totalPages)} disabled={safePage >= totalPages}
                      className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&raquo;</button>
                  </div>
                </div>
              )}
              </div>
              {paginated.map((u) => (
              <div
                key={u.id}
                className="bg-white rounded-lg border border-gray-200 px-5 py-4"
              >
                {editId === u.id ? (
                  /* Edit mode */
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[11px] text-txt-secondary uppercase mb-0.5">Name</label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-txt-secondary uppercase mb-0.5">Email</label>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-txt-secondary uppercase mb-0.5">Role</label>
                        <select
                          value={editForm.role}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value, clientId: e.target.value === 'ADMIN' ? '' : editForm.clientId })}
                          className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                        >
                          <option value="CLIENT">Client</option>
                          <option value="ADMIN">Admin</option>
                        </select>
                      </div>
                      {editForm.role === 'CLIENT' && (
                        <div>
                          <label className="block text-[11px] text-txt-secondary uppercase mb-0.5">Client <span className="text-red-400">*</span></label>
                          <select
                            value={editForm.clientId}
                            onChange={(e) => setEditForm({ ...editForm, clientId: e.target.value })}
                            className="w-full rounded-lg border-gray-300 text-sm focus:border-gold focus:ring-gold"
                          >
                            <option value="">Select...</option>
                            {clients.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    {editError && <p className="text-red-500 text-xs">{editError}</p>}
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(u.id)} className="btn-gold text-xs">Save</button>
                      <button onClick={() => setEditId(null)} className="text-xs text-txt-secondary hover:text-txt-primary">Cancel</button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div className="flex items-center gap-4">
                    <input type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        e.target.checked ? next.add(u.id) : next.delete(u.id);
                        setSelected(next);
                      }}
                      className="rounded border-gray-300 text-gold focus:ring-gold flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-txt-primary">{u.name}</span>
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                          u.role === 'ADMIN'
                            ? 'bg-gold/20 text-gold-dark'
                            : 'bg-teal/20 text-teal-dark'
                        }`}>
                          {u.role}
                        </span>
                        {u.clientId && (
                          <span className="text-xs text-txt-secondary">
                            {clientName(u.clientId)}
                          </span>
                        )}
                        {u.trialExpiresAt && (
                          <button
                            onClick={() => { setTrialId(trialId === u.id ? null : u.id); setTrialDays('30'); }}
                            className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded cursor-pointer hover:ring-1 hover:ring-offset-1 ${
                              new Date(u.trialExpiresAt) > new Date()
                                ? 'bg-blue-100 text-blue-700 hover:ring-blue-400'
                                : 'bg-red-100 text-red-700 hover:ring-red-400'
                            }`}
                          >
                            {new Date(u.trialExpiresAt) > new Date()
                              ? `Trial (expires ${new Date(u.trialExpiresAt).toLocaleDateString()})`
                              : 'Trial Expired'}
                          </button>
                        )}
                        {u.emailVerified === false && (
                          <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                            Unverified
                          </span>
                        )}
                      </div>
                      {trialId === u.id && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <button
                            onClick={async () => {
                              await manageUserTrial(u.id, 'extend', parseInt(trialDays));
                              setTrialId(null);
                              const updated = await getUsers();
                              setUsers(updated);
                            }}
                            className="text-[11px] bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                          >
                            Extend
                          </button>
                          <input type="number" value={trialDays} onChange={(e) => setTrialDays(e.target.value)}
                            className="w-14 text-xs border border-gray-300 rounded px-1.5 py-1 focus:ring-gold focus:border-gold" />
                          <span className="text-[11px] text-txt-secondary">days</span>
                          <button
                            onClick={async () => {
                              await manageUserTrial(u.id, 'promote', null, u.clientId);
                              setTrialId(null);
                              const updated = await getUsers();
                              setUsers(updated);
                            }}
                            className="text-[11px] bg-green-50 text-green-700 px-2 py-1 rounded hover:bg-green-100"
                          >
                            Promote to Full
                          </button>
                          <button
                            onClick={async () => {
                              await manageUserTrial(u.id, 'revoke');
                              setTrialId(null);
                              const updated = await getUsers();
                              setUsers(updated);
                            }}
                            className="text-[11px] bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100"
                          >
                            Revoke
                          </button>
                          <button onClick={() => setTrialId(null)} className="text-[11px] text-txt-secondary hover:text-txt-primary">
                            Cancel
                          </button>
                        </div>
                      )}
                      <p className="text-sm text-txt-secondary truncate">{u.email}</p>
                    </div>

                    {resetId === u.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="New password (12+ chars)"
                          autoFocus
                          className="rounded-lg border-gray-300 text-sm w-44 focus:border-gold focus:ring-gold"
                        />
                        <button
                          onClick={() => handleResetPassword(u.id)}
                          disabled={resetting || !!checkPassword(newPassword)}
                          className="btn-gold text-xs disabled:opacity-40"
                        >
                          {resetting ? '...' : 'Set'}
                        </button>
                        <button
                          onClick={() => { setResetId(null); setNewPassword(''); }}
                          className="text-xs text-txt-secondary hover:text-txt-primary"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEdit(u)}
                          className="p-2 text-gray-400 hover:text-navy transition-colors"
                          title="Edit user"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setResetId(u.id); setNewPassword(''); setEditId(null); }}
                          className="p-2 text-gray-400 hover:text-gold transition-colors"
                          title="Reset password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        {u.id !== currentUser.id && (
                          <button
                            onClick={() => handleDelete(u.id, u.name)}
                            className="p-2 text-gray-300 hover:text-red-500 transition-colors"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {filtered.length === 0 && (
              <p className="text-center text-txt-secondary text-sm py-8">No users found.</p>
            )}
            {filtered.length > PAGE_SIZE && (
              <div className="flex items-center justify-end mt-2 text-sm text-txt-secondary">
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(1)} disabled={safePage <= 1}
                    className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&laquo;</button>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                    className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&lsaquo;</button>
                  <span className="px-3 py-1 text-txt-primary font-semibold">{safePage} / {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                    className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&rsaquo;</button>
                  <button onClick={() => setPage(totalPages)} disabled={safePage >= totalPages}
                    className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&raquo;</button>
                </div>
              </div>
            )}
            </>); })()}
          </div>
        )}
      </main>
    </div>
  );
}
